import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { getBookingImportJob } from '../../BulkUploadPage/apis/bookingImportApi';

/**
 * Polls a booking import job until it reaches a terminal state (imported or failed).
 * On completion, invalidates both the uploaded-receipts cache and the single receipt cache.
 */
export const useBookingImportJobPolling = (
  jobId: string | null,
  receiptId: string | null
) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const { data: job } = useQuery({
    queryKey: ['booking-import-job', jobId],
    queryFn: async () => {
      if (!jobId) return null;
      const token = await getAccessTokenSilently();
      return getBookingImportJob(jobId, token);
    },
    enabled: !!jobId,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data) return 5000;
      if (data.status === 'imported' || data.status === 'failed') return false;
      return 5000;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 60 * 1000,
  });

  const hasInvalidated = useRef(false);

  useEffect(() => {
    if (!job) return;
    if (job.status !== 'imported' && job.status !== 'failed') {
      hasInvalidated.current = false;
      return;
    }
    if (hasInvalidated.current) return;
    hasInvalidated.current = true;

    queryClient.invalidateQueries({ queryKey: ['uploaded-receipts'] });
    if (receiptId) {
      queryClient.invalidateQueries({
        queryKey: ['uploaded-receipt', receiptId],
      });
    }
  }, [job?.status, receiptId, queryClient]);

  return { job };
};
